import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen,
  Wallet,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  GraduationCap,
  Coins,
  Flame,
  Trophy,
  ShoppingBag,
  Award,
} from "lucide-react";

export default function StudentOverview() {
  const { user } = useAuth();
  const [enrolments, setEnrolments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [achievements, setAchievements] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [e, c, w, a, p] = await Promise.all([
          api.get("/enrollments/me"),
          api.get("/certificates/me"),
          api.get("/wallet/me"),
          api.get("/student/achievements"),
          api.get("/store/products"),
        ]);
        setEnrolments(e.data);
        setCerts(c.data);
        setWallet(w.data);
        setAchievements(a.data);
        setFeatured(p.data.filter((x) => x.featured).slice(0, 3));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const avgProgress =
    enrolments.length === 0
      ? 0
      : Math.round(
          enrolments.reduce((s, x) => s + (x.enrollment?.progress_pct || 0), 0) /
            enrolments.length
        );
  const earnedCount = achievements.filter((a) => a.earned).length;

  return (
    <div className="p-6 md:p-10">
      {/* Top welcome */}
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Student · Console
        </div>
        <h1 className="font-display mt-1 text-4xl font-extrabold tracking-tighter sm:text-5xl">
          Hello, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your academic identity is verified on the simulated Polygon network.
        </p>
      </div>

      {/* MWZ Hero strip */}
      <div className="mb-8 grid grid-cols-1 gap-px bg-foreground md:grid-cols-2">
        <Link
          to="/app/student/rewards"
          className="group relative overflow-hidden bg-foreground p-6 text-background transition-all hover:bg-foreground/90"
          data-testid="mwz-hero-card"
        >
          <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full border border-primary/30"></div>
          <div className="absolute -bottom-6 -right-2 h-20 w-20 rounded-full border border-accent/40"></div>
          <div className="relative flex items-start justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-background/60">
                Mowanzi Wallet
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-display text-4xl font-extrabold tracking-tighter">
                  {(user?.mwz_balance || 0).toLocaleString()}
                </div>
                <div className="mono text-sm font-bold text-primary">MWZ</div>
              </div>
              <div className="mono mt-2 flex items-center gap-3 text-[10px] uppercase tracking-widest text-background/60">
                <span className="flex items-center gap-1">
                  <Flame className="h-3 w-3 text-accent" />
                  {user?.streak_days || 0}-day streak
                </span>
                <span>·</span>
                <span>+1000 MWZ / module</span>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        <Link
          to="/app/student/store"
          className="group relative overflow-hidden bg-primary p-6 text-primary-foreground transition-all"
          data-testid="store-hero-card"
        >
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full border border-primary-foreground/30"></div>
          <div className="relative flex items-start justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-primary-foreground/70">
                Online Store
              </div>
              <div className="font-display mt-2 text-2xl font-extrabold leading-tight tracking-tighter">
                Books · Hardware · Coaching
              </div>
              <div className="mt-2 text-sm text-primary-foreground/80">
                10+ items — spend your MWZ or pay in USD.
              </div>
            </div>
            <ShoppingBag className="h-6 w-6" />
          </div>
        </Link>
      </div>

      {/* Bento KPI grid */}
      <div className="mb-10 grid grid-cols-2 gap-px bg-foreground sm:grid-cols-3 lg:grid-cols-5">
        <Kpi icon={<BookOpen className="h-5 w-5" />} label="Active Courses" value={enrolments.length} />
        <Kpi icon={<GraduationCap className="h-5 w-5" />} label="Avg Progress" value={`${avgProgress}%`} />
        <Kpi icon={<ShieldCheck className="h-5 w-5" />} label="Certificates" value={certs.length} />
        <Kpi icon={<Trophy className="h-5 w-5" />} label="Achievements" value={`${earnedCount}/${achievements.length}`} />
        <Kpi icon={<Wallet className="h-5 w-5" />} label="USD Wallet" value={`$${wallet.balance.toFixed(0)}`} accent />
      </div>

      {/* Continue learning */}
      <div className="mb-10">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              /continue learning
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">
              Pick up where you left off
            </h2>
          </div>
          <Link
            to="/app/student/courses"
            className="mono text-[11px] uppercase tracking-widest hover:underline"
            data-testid="see-all-courses-link"
          >
            See all →
          </Link>
        </div>

        {loading ? (
          <Skeleton />
        ) : enrolments.length === 0 ? (
          <EmptyEnrol />
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {enrolments.slice(0, 3).map(({ enrollment, course }) => (
              <ContinueCard key={enrollment.id} enrol={enrollment} course={course} />
            ))}
          </div>
        )}
      </div>

      {/* Achievements snapshot */}
      {achievements.length > 0 && (
        <div className="mb-10">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                /badges
              </div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight">
                Recent achievements
              </h2>
            </div>
            <Link to="/app/student/rewards" className="mono text-[11px] uppercase tracking-widest hover:underline">
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-4 gap-3 md:grid-cols-8">
            {achievements.map((a) => (
              <div
                key={a.code}
                className={`border-2 p-3 text-center ${
                  a.earned ? "border-foreground bg-background hard-shadow-sm" : "border-dashed border-foreground/30 bg-muted/30 opacity-50"
                }`}
                title={a.description}
              >
                <Award className={`mx-auto h-5 w-5 ${a.earned ? "text-accent" : "text-muted-foreground"}`} />
                <div className="font-display mt-1.5 text-[10px] font-extrabold leading-tight">
                  {a.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store featured */}
      {featured.length > 0 && (
        <div className="mb-10">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                /featured in store
              </div>
              <h2 className="font-display text-2xl font-extrabold tracking-tight">
                Spend your MWZ
              </h2>
            </div>
            <Link to="/app/student/store" className="mono text-[11px] uppercase tracking-widest hover:underline">
              Open store →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {featured.map((p) => (
              <Link
                key={p.id}
                to="/app/student/store"
                className="group flex border-2 border-foreground bg-background overflow-hidden hover-lift"
                data-testid={`featured-store-${p.id}`}
              >
                <img src={p.image_url} alt={p.name} className="h-28 w-28 shrink-0 border-r-2 border-foreground object-cover" />
                <div className="flex flex-1 flex-col p-3">
                  <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    {p.category}
                  </div>
                  <div className="font-display mt-1 line-clamp-2 text-sm font-extrabold leading-tight tracking-tight">
                    {p.name}
                  </div>
                  <div className="mt-auto flex items-center gap-2 pt-2">
                    <span className="mono text-xs font-bold">${p.price_usd.toFixed(0)}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="mono flex items-center gap-0.5 text-xs font-bold text-primary">
                      <Coins className="h-3 w-3" />
                      {p.price_mwz.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Latest cert */}
      {certs.length > 0 && (
        <div className="mb-10">
          <div className="mb-4">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              /latest credential
            </div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">
              Your most recent certificate
            </h2>
          </div>
          <MiniCert cert={certs[0]} />
        </div>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, accent }) {
  return (
    <div className={`${accent ? "bg-primary text-primary-foreground" : "bg-background"} p-6`}>
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center ${
            accent ? "bg-primary-foreground text-primary" : "bg-foreground text-background"
          }`}
        >
          {icon}
        </div>
      </div>
      <div className="mt-6 font-display text-3xl font-extrabold tracking-tight">
        {value}
      </div>
      <div
        className={`mono mt-1 text-[10px] uppercase tracking-widest ${
          accent ? "text-primary-foreground/70" : "text-muted-foreground"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function ContinueCard({ enrol, course }) {
  if (!course) return null;
  return (
    <Link
      to={`/app/student/learn/${course.id}`}
      className="group block border-2 border-foreground bg-background p-5 hover-lift"
      data-testid={`continue-card-${course.slug}`}
    >
      <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        {course.category}
      </div>
      <div className="font-display text-lg font-extrabold leading-tight tracking-tight">
        {course.title}
      </div>
      <div className="mt-5">
        <Progress value={enrol.progress_pct || 0} className="h-2 rounded-none" />
        <div className="mono mt-2 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>{enrol.progress_pct?.toFixed(0) || 0}% complete</span>
          <span>{enrol.completed_lessons?.length || 0} lessons</span>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest">Continue</div>
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </div>
    </Link>
  );
}

function MiniCert({ cert }) {
  return (
    <Link
      to="/app/student/certificates"
      className="cert-grain block border-2 border-foreground bg-white p-6 hover-lift"
      data-testid="latest-cert-link"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Certificate · {cert.course_category}
          </div>
          <div className="font-display mt-1 text-2xl font-extrabold tracking-tighter">
            {cert.course_title}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            Issued to {cert.student_name}
          </div>
        </div>
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
      <div className="mt-4 break-all border border-foreground/15 bg-muted p-3 font-mono text-[10px]">
        cert_hash: {cert.cert_hash}
      </div>
    </Link>
  );
}

function EmptyEnrol() {
  return (
    <div className="border-2 border-dashed border-foreground/30 bg-background p-10 text-center">
      <div className="font-display text-xl font-extrabold tracking-tight">
        No enrolments yet
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Browse the catalogue and enrol in your first programme.
      </p>
      <Link to="/app/student/catalog">
        <Button className="mt-5 rounded-none bg-foreground text-background hard-shadow hover-lift" data-testid="browse-catalog-cta">
          Browse Catalogue →
        </Button>
      </Link>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-40 animate-pulse border border-border bg-muted" />
      ))}
    </div>
  );
}
